/**
 * ST-1 — Site Tags: the firm's own blanks.
 *
 * A strip of numbered stickers lives in the truck. A tag means nothing until
 * it is scanned on a walk (ST-2), so this page is the firm's ledger of them:
 * what is placed, what is open, and what is still blank.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DockPanel from '../components/shell/DockPanel';
import { useNavigate } from 'react-router-dom';
import { printPdf } from '../utils/printPdf';
import '../styles/site-tags.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'done', label: 'Done' },
  { key: 'no_project', label: 'No project' },
  { key: 'mine', label: 'Mine' },
  { key: 'blank', label: 'Blanks' },
];

const PILL = { blank: ['kst-pill', 'Blank'], open: ['kst-pill kst-pill--late', 'Open'],
  done: ['kst-pill kst-pill--ok', 'Done'], void: ['kst-pill', 'Void'] };

const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric' }) : '');

export default function SiteTagsPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [tags, setTags] = useState([]);
  const [chip, setChip] = useState('all');
  const [panel, setPanel] = useState(false);
  const [count, setCount] = useState(10);
  const [label, setLabel] = useState('');
  const [stock, setStock] = useState('ql62');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (chip === 'mine') params.mine = 1;
    else if (chip !== 'all') params.state = chip;
    if (chip.startsWith('project:')) {
      delete params.state;
      params.project_id = chip.slice(8);
    }
    const [list, tally] = await Promise.all([
      axios.get(`${API}/site-tags`, { params }),
      axios.get(`${API}/site-tags/counts`),
    ]);
    setTags(list.data.tags || []);
    setCounts(tally.data);
  }, [chip]);

  useEffect(() => { load().catch(() => setTags([])); }, [load]);

  // ST-2 — the walk opens in its own small window, as the My Desk pop-outs do.
  const openScan = (event) => {
    if (event) event.preventDefault();
    const width = 420;
    const height = 820;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open('/widget/tags', 'keymade-sitetags',
      `width=${width},height=${height},left=${left},top=${top},`
      + 'resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no');
  };

  const openStrip = () => {
    setProblem('');
    setLabel(`Strip ${(counts?.strips?.length || 0) + 1}`);
    setPanel(true);
  };

  const makeStrip = async () => {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1 || n > 20) {
      setProblem('A strip holds between 1 and 20 tags');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/site-tags/strips`,
        { count: n, label, stock });
      setPanel(false);
      await load();
      printPdf(`${API}/site-tags/strips/${data.strip_id}/print.pdf`);
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That strip was not made');
    } finally { setBusy(false); }
  };

  const projects = [...new Map(tags.filter((t) => t.project_id)
    .map((t) => [t.project_id, t.project_name || 'A project'])).entries()];

  return (
    <div className="kst" data-testid="site-tags-page">
      <div className="kst-hd">
        <div>
          <h1 className="kst-t">Site Tags</h1>
          <p className="kst-k" data-testid="site-tags-counts">
            {counts?.placed || 0} placed · {counts?.open || 0} open · {counts?.done || 0} done
            {' · '}{counts?.blank || 0} blanks in the truck
          </p>
        </div>
        <div className="kst-acts">
          <a className="kst-btn kst-btn--link" href="/widget/tags" onClick={openScan}
            data-testid="site-tags-scan-button">Scan</a>
          <button type="button" className="kst-btn kst-btn--brand" onClick={openStrip}
            data-testid="site-tags-print">Print a strip of blanks</button>
        </div>
      </div>

      <div className="kst-chips">
        {CHIPS.map((c) => (
          <button type="button" key={c.key} className={`kst-chip${chip === c.key ? ' is-on' : ''}`}
            onClick={() => setChip(c.key)} data-testid={`site-tags-chip-${c.key}`}>{c.label}</button>
        ))}
        {projects.map(([id, name]) => (
          <button type="button" key={id}
            className={`kst-chip${chip === `project:${id}` ? ' is-on' : ''}`}
            onClick={() => setChip(`project:${id}`)} data-testid={`site-tags-chip-project-${id}`}>
            {name}
          </button>
        ))}
      </div>

      {tags.length === 0 ? (
        <div className="kst-none" data-testid="site-tags-empty">
          <p>
            No site tags yet. Print a strip of blanks, keep them in the truck, and a tag
            means nothing until you scan it on a walk.
          </p>
          <button type="button" className="kst-btn kst-btn--brand" onClick={openStrip}
            data-testid="site-tags-print-empty">Print a strip of blanks</button>
        </div>
      ) : (
        <table className="kst-table" data-testid="site-tags-table">
          <thead>
            <tr><th>Tag</th><th>What</th><th>Project · room</th><th>Trade</th><th>Placed</th><th>State</th></tr>
          </thead>
          <tbody>
            {tags.map((t) => (
              <tr key={t.id} data-testid={`site-tags-row-${t.tag_number}`}
                className={t.state === 'blank' ? '' : 'kst-row--open'}
                onClick={t.state === 'blank' ? undefined
                  : () => navigate(`/site-tags/${t.id}`)}>
                <td className="kst-no">{t.tag_number}</td>
                <td>{t.title || '—'}</td>
                <td>
                  {t.state === 'blank' ? <span className="kst-muted">Blank</span>
                    : t.project_name
                      ? `${t.project_name}${t.room ? ` · ${t.room}` : ''}`
                      : <span className="kst-muted">No project · firm</span>}
                </td>
                <td>{t.trade || '—'}</td>
                <td>{t.placed_by_name ? `${t.placed_by_name} · ${day(t.placed_at)}` : '—'}</td>
                <td>
                  <span className={(PILL[t.state] || PILL.blank)[0]}>
                    {(PILL[t.state] || PILL.blank)[1]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="kst-foot" data-testid="site-tags-strips">
        {(counts?.strips || []).map((s) => (
          <p className="kst-strip" key={s.strip_id}>
            Blanks {s.numbers} · printed {day(s.printed_at) || 'not yet'} · {s.label}
            {' · '}{s.stock === 'ql29' ? '29 mm' : '62 mm'}
            <button type="button" className="kst-btn" data-testid={`site-tags-reprint-${s.strip_id}`}
              onClick={() => printPdf(`${API}/site-tags/strips/${s.strip_id}/print.pdf`)}>
              Reprint
            </button>
          </p>
        ))}
      </div>

      {panel && (
        <DockPanel onClose={() => setPanel(false)} width="460px">
          <aside className="kmt-panel kst-panel" data-testid="site-tags-strip-panel">
            <div className="kp-head">
              <span className="kst-k">A strip of blanks</span>
            </div>
            <div className="kst-form">
            <label className="kst-f">
              <span>How many</span>
              <input className="in" type="number" min="1" max="20" value={count}
                onChange={(e) => setCount(e.target.value)} data-testid="site-tags-strip-count" />
            </label>
            <label className="kst-f">
              <span>Label</span>
              <input className="in" value={label} onChange={(e) => setLabel(e.target.value)}
                data-testid="site-tags-strip-label" />
            </label>
            <div className="kst-f" data-testid="site-tags-strip-stock">
              <span>Stock</span>
              <div className="kst-tiles">
                {[['ql62', '62 mm'], ['ql29', '29 mm']].map(([id, name]) => (
                  <button type="button" key={id} className={`kst-tile${stock === id ? ' is-on' : ''}`}
                    onClick={() => setStock(id)} data-testid={`site-tags-strip-stock-${id}`}>
                    {name}
                  </button>
                ))}
              </div>
            </div>
            {problem && <p className="kst-err" data-testid="site-tags-strip-error">{problem}</p>}
            <button type="button" className="kst-btn kst-btn--brand" disabled={busy}
              onClick={makeStrip} data-testid="site-tags-strip-make">Make the strip</button>
            </div>
          </aside>
        </DockPanel>
      )}
    </div>
  );
}
